import { uniq } from 'lodash';
import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { FULL_SCOPES_ACCOUNT_ADMIN } from '@dadohr/shared/domainTypes/permissions';
import { FS } from '@dadohr/shared/versions';
import { adminUsersCollection } from '../../../services/adminUsers';
import { toAdminUserDoc } from '../../../services/adminUsers/documents';
import { store, updateInTransaction } from '../../../services/firebase';
import {
  CollectionListener,
  createCollectionListenerStore,
  createSingleCollectionListenerStore,
  useCollectionListener
} from '../../../services/firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../services/firecache/documentListener';
import { now } from '../../../services/time';
import ENV from '../../env.json';

const adminUserbyIdStore = createDocumentListenerGetter(
  (id) => adminUsersCollection().doc(id),
  toAdminUserDoc
);

export const useAdminUserById = (id: string) =>
  useDocumentListener(adminUserbyIdStore(id));

const adminUsersByOrgStore = createCollectionListenerStore(
  (orgId) =>
    new CollectionListener(
      adminUsersCollection().where('orgs', 'array-contains', orgId),
      toAdminUserDoc
    )
);

export const useAdminUsersInOrg = (orgId: string) => {
  return useCollectionListener(adminUsersByOrgStore(orgId));
};

const allAdminUsersStore = createSingleCollectionListenerStore(
  () =>
    new CollectionListener(adminUsersCollection(), toAdminUserDoc, {
      ignoreInitialCachedResults: true
    })
);

export const getAllAdminUsers = () => {
  return allAdminUsersStore().get();
};

export const useAllAdminUsers = () => {
  return useCollectionListener(allAdminUsersStore());
};

export const getAdminUsersForSysAdmins = async () => {
  const ids = new Set(Object.keys(ENV.users));
  // ask by id later on
  const allAdmins = await getAllAdminUsers();
  return allAdmins.filter((a) => ids.has(a.id));
};

export const addToOrg = async (adminUserId: string, orgId: string) => {
  await updateInTransaction<IAdminUser>(
    adminUsersCollection().doc(adminUserId),
    async (d) => {
      if (!d) {
        return null;
      }
      return { orgs: uniq([...d.orgs, orgId]) };
    }
  );
  await updateInTransaction<IOrganization>(
    store().collection(FS.orgs).doc(orgId),
    async (d) => {
      if (!d) {
        return null;
      }
      return {
        permissions: {
          ...d.permissions,
          [adminUserId]: FULL_SCOPES_ACCOUNT_ADMIN()
        }
      };
    }
  );
};

/**
 * @deprecated Use the cloud function instead
 */
export const removeFromOrg = async (adminUserId: string, orgId: string) => {
  await updateInTransaction<IAdminUser>(
    adminUsersCollection().doc(adminUserId),
    async (d) => {
      if (!d) {
        return null;
      }
      return { orgs: d.orgs.filter((o) => o !== orgId) };
    }
  );
  await updateInTransaction<IOrganization>(
    store().collection(FS.orgs).doc(orgId),
    async (d) => {
      if (!d) {
        return null;
      }
      delete d.permissions[adminUserId];
      return {
        permissions: d.permissions
      };
    }
  );
};

export const getNewAdminUserdoc = (
  id: string,
  {
    email,
    firstName = '',
    middleName = '',
    lastName = '',
    preferredName = '',
    avatar = ''
  }: {
    email: string;
  } & Partial<{
    firstName: string;
    middleName: string;
    lastName: string;
    preferredName: string;

    avatar: string;
  }>
) => {
  const doc: Doc<IAdminUser> = {
    id,
    collection: FS.adminUsers,
    data: {
      email,
      firstName,
      middleName,
      lastName,
      preferredName,
      avatar,
      createdBy: 'EMPLEXP',
      createdAt: now(),
      orgs: []
    }
  };
  return doc;
};
